import { useState } from "react";
import api from "@/lib/api";
import { Search, Sparkles, PieChart, Layers, Landmark } from "lucide-react";
import { toast } from "sonner";

const CONFIG = {
  etf: { title: "ETF Research", icon: Layers, placeholder: "e.g. VOO, iShares Core S&P 500, SPY", label: "ETF" },
  mutual: { title: "Mutual Fund Research", icon: PieChart, placeholder: "e.g. Vanguard 500 Index, Fidelity Contrafund", label: "mutual fund" },
  money_market: { title: "Money Market Fund Research", icon: Landmark, placeholder: "e.g. Vanguard Federal Money Market, Stanbic Money Market", label: "money market fund" },
};

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-stone-100 py-2.5 last:border-0">
      <span className="text-sm text-stone-500">{label}</span>
      <span className="text-right text-sm font-medium text-stone-900">{value ?? "—"}</span>
    </div>
  );
}

function Alloc({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <p className="mb-2 text-sm font-semibold text-stone-900">{title}</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.name || i}>
            <div className="flex justify-between text-sm"><span className="text-stone-600">{it.name}</span><span className="tabular text-stone-800">{it.pct}</span></div>
            <div className="mt-1 h-1.5 w-full rounded-full bg-stone-100">
              <div className="h-1.5 rounded-full bg-emerald-700" style={{ width: `${parseFloat(it.pct) || 5}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FundResearch({ fundType }) {
  const cfg = CONFIG[fundType];
  const Icon = cfg.icon;
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rep, setRep] = useState(null);

  const analyze = async () => {
    if (!q) return;
    setLoading(true); setRep(null);
    try {
      const { data } = await api.post("/fund/analyze", { query: q, fund_type: fundType });
      setRep(data.ai);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not analyze this fund."); }
    finally { setLoading(false); }
  };

  const isMM = fundType === "money_market";

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 md:px-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500"><Icon size={14} strokeWidth={1.5} /> Fund Research</p>
      <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900 sm:text-4xl">{cfg.title}</h1>

      <div className="mt-5 flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
          <input data-testid="fund-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && analyze()}
            placeholder={cfg.placeholder}
            className="h-12 w-full rounded-lg border border-stone-200 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
        </div>
        <button data-testid="fund-analyze-btn" disabled={!q || loading} onClick={analyze}
          className="flex h-12 items-center gap-2 rounded-lg bg-navy px-5 text-sm font-medium text-white transition-colors hover:bg-navyd disabled:opacity-60">
          <Sparkles size={16} strokeWidth={1.5} /> Analyze
        </button>
      </div>

      {loading && (
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-6" data-testid="fund-loading">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-stone-300 border-t-navy" />
          <p className="text-sm text-stone-600">Researching {cfg.label} with GPT-5.5…</p>
        </div>
      )}

      {rep && !loading && (
        <div className="mt-6 space-y-5 animate-in-up" data-testid="fund-report">
          <div className="rounded-xl border border-stone-200 bg-white p-6">
            <h2 className="font-head text-2xl font-medium tracking-tight text-stone-900">{rep.fund_name}</h2>
            <p className="mt-1 text-sm text-stone-500">{rep.ticker} · {rep.fund_provider} · {rep.country}</p>
            <div className="mt-4 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div>
                <Row label="Currency" value={rep.currency} />
                <Row label="Risk level" value={rep.risk_level} />
                {isMM ? <>
                  <Row label="Yield" value={rep.yield} />
                  <Row label="Minimum investment" value={rep.minimum_investment} />
                  <Row label="Liquidity" value={rep.liquidity} />
                </> : <>
                  <Row label="Expense ratio" value={rep.expense_ratio} />
                  <Row label="Distribution yield" value={rep.distribution_yield} />
                  <Row label="Distribution frequency" value={rep.distribution_frequency} />
                </>}
              </div>
              <div>
                {isMM ? <>
                  <Row label="Withdrawal rules" value={rep.withdrawal_rules} />
                  <Row label="Fees" value={rep.fees} />
                  <Row label="Best use case" value={rep.best_use_case} />
                </> : <>
                  <Row label="Best investor type" value={rep.best_investor_type} />
                  <Row label="Historical performance" value={rep.historical_performance} />
                </>}
              </div>
            </div>
          </div>

          {isMM && rep.emergency_fund_suitability && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
              <p className="text-sm font-semibold text-navy">Emergency fund suitability</p>
              <p className="mt-1 text-sm text-stone-700">{rep.emergency_fund_suitability}</p>
            </div>
          )}

          {!isMM && (
            <>
              {rep.top_holdings?.length > 0 && (
                <div className="rounded-xl border border-stone-200 bg-white p-6">
                  <p className="mb-3 text-sm font-semibold text-stone-900">Top holdings</p>
                  <div className="flex flex-wrap gap-2">
                    {rep.top_holdings.map((h, i) => (
                      <span key={i} className="rounded-full bg-stone-100 px-3 py-1 text-sm text-stone-700">{i + 1}. {h}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Alloc title="Sector allocation" items={rep.sector_allocation} />
                <Alloc title="Country exposure" items={rep.country_exposure} />
                <Alloc title="Asset allocation" items={rep.asset_allocation} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-stone-200 bg-white p-5">
                  <p className="text-sm font-semibold text-navy">Pros</p>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(rep.pros || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
                <div className="rounded-xl border border-stone-200 bg-white p-5">
                  <p className="text-sm font-semibold text-rose-700">Cons</p>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(rep.cons || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
                  <p className="text-sm font-semibold text-navy">Bull case</p>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(rep.bull_case || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-5">
                  <p className="text-sm font-semibold text-rose-700">Bear case</p>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{(rep.bear_case || []).map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              </div>
            </>
          )}

          {rep.summary && (
            <div className="rounded-xl bg-stone-900 p-5 text-sm text-stone-100">
              <span className="font-semibold text-emerald-300">Beginner summary: </span>{rep.summary}
            </div>
          )}
          <p className="text-center text-xs text-stone-400">This is for educational purposes only and does not constitute financial advice.</p>
        </div>
      )}
    </main>
  );
}
